import React, { useState, useEffect, useMemo } from 'react';
import { useStore } from '../context/StoreContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase'; 
import { collection, getDocs, query, where } from 'firebase/firestore'; 

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { 
        style: 'currency', currency: 'KES', minimumFractionDigits: 0, maximumFractionDigits: 0, 
    }).format(amount);
};

const Cart = () => {
    const { cart, removeFromCart, updateQuantity, clearCart } = useStore();
    const navigate = useNavigate();
    const [cartItemsDetails, setCartItemsDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCartDetails = async () => {
            if (cart.length === 0) {
                setCartItemsDetails([]);
                setLoading(false);
                return;
            }
            const productIds = cart.map(item => item.id);
            if (productIds.length > 0) {
                try {
                    const productsCollectionRef = collection(db, 'products');
                    const q = query(productsCollectionRef, where('__name__', 'in', productIds.slice(0, 10)));
                    const querySnapshot = await getDocs(q);
                    
                    const detailsMap = {};
                    querySnapshot.forEach(doc => { detailsMap[doc.id] = { id: doc.id, ...doc.data() }; });

                    const mergedItems = cart.map(cartItem => ({
                        ...(detailsMap[cartItem.id] || {}),
                        quantity: cartItem.quantity,
                        name: detailsMap[cartItem.id]?.name || "Item",
                        price: detailsMap[cartItem.id]?.price || 0,
                        imageUrl: detailsMap[cartItem.id]?.imageUrl || ''
                    }));
                    setCartItemsDetails(mergedItems);
                } catch (err) {
                    console.error("Error fetching cart details:", err);
                    setError("Failed to load product details.");
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchCartDetails();
    }, [cart]); 

    const totals = useMemo(() => {
        const subtotal = cartItemsDetails.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);
        const total = subtotal; 
        return { subtotal, total };
    }, [cartItemsDetails]);

    if (loading) return <div className="container mx-auto px-4 py-8 text-center"><h2>Loading cart...</h2></div>;
    if (error) return <div className="container mx-auto px-4 py-8 text-center"><h2>Error: {error}</h2></div>;

    if (cart.length === 0) {
        return (
            <div className="container mx-auto px-4 py-8 text-center">
                <h1 className="text-3xl font-bold mb-4">Your Shopping Cart is Empty</h1>
                <p className="text-gray-600 mb-8">Browse our featured products to find what you need.</p>
                <button className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700" onClick={() => navigate('/')}>
                    Start Shopping
                </button>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-8">Your Shopping Cart</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                    <div className="space-y-4">
                        {cartItemsDetails.map(item => (
                            <div key={item.id} className="flex items-center bg-white p-4 rounded-lg shadow">
                                <img 
                                    src={item.imageUrl || 'https://via.placeholder.com/90'} 
                                    alt={item.name} 
                                    className="w-24 h-24 object-cover rounded-md cursor-pointer"
                                    onClick={() => navigate(`/product/${item.id}`)}
                                />
                                <div className="flex-grow ml-4">
                                    <h2 className="text-lg font-bold cursor-pointer hover:text-blue-600" onClick={() => navigate(`/product/${item.id}`)}>{item.name}</h2>
                                    <p className="text-sm text-gray-600">Brand: {item.brand || 'N/A'}</p>
                                    <p className="text-sm font-semibold">{formatCurrency(item.price)} each</p>
                                </div>
                                <div className="flex items-center mx-4">
                                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)} disabled={item.quantity <= 1} className="px-3 py-1 bg-gray-200 rounded-l-md">-</button>
                                    <input type="number" value={item.quantity} readOnly className="w-12 text-center border-t border-b" />
                                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="px-3 py-1 bg-gray-200 rounded-r-md">+</button>
                                </div>
                                <div className="text-lg font-bold mx-4">
                                    {formatCurrency((item.price || 0) * item.quantity)}
                                </div>
                                <button className="text-red-600 hover:text-red-800" onClick={() => removeFromCart(item.id)}>
                                    &#10005;
                                </button>
                            </div>
                        ))}
                    </div>
                    <button onClick={clearCart} className="mt-4 bg-red-100 text-red-600 px-4 py-2 rounded-md hover:bg-red-200">Clear Cart</button>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow">
                    <h2 className="text-xl font-bold mb-4">Order Summary</h2>
                    <div className="flex justify-between mb-2">
                        <span>Subtotal:</span>
                        <span>{formatCurrency(totals.subtotal)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg mt-4 pt-4 border-t">
                        <span>Order Total:</span>
                        <span>{formatCurrency(totals.total)}</span>
                    </div>
                    <button className="w-full bg-blue-600 text-white mt-6 py-3 rounded-md hover:bg-blue-700" onClick={() => navigate('/checkout')}>
                        Proceed to Checkout
                    </button>
                    <p className="text-sm text-gray-600 text-center mt-2">Shipping calculated at checkout.</p>
                </div>
            </div>
        </div>
    );
};

export default Cart;